/**
 * Guest Search Rate Limiting
 *
 * Limits unauthenticated users to a configurable number of searches per day.
 * Resets automatically at midnight (local time).
 *
 * Configuration is centralized here for easy adjustment.
 */

// ============================================================================
// CONFIGURATION - Easy to modify
// ============================================================================

export const GUEST_DAILY_SEARCH_LIMIT = 50

// Warning threshold - show "X remaining" when below this number
export const GUEST_SEARCH_WARNING_THRESHOLD = 10

// ============================================================================
// Internal Implementation
// ============================================================================

const STORAGE_KEY = 'miaozi_guest_search_tracking'

interface SearchTracking {
  count: number
  resetDate: string // ISO date string (YYYY-MM-DD)
}

/**
 * Get current date in YYYY-MM-DD format (local timezone)
 */
function getTodayString(): string {
  return new Date().toISOString().split('T')[0]
}

/**
 * Get the current search status for guest users
 */
export function getGuestSearchStatus(): {
  remaining: number
  isLimited: boolean
  count: number
} {
  if (typeof window === 'undefined') {
    return {
      remaining: GUEST_DAILY_SEARCH_LIMIT,
      isLimited: false,
      count: 0
    }
  }

  const today = getTodayString()
  const stored = localStorage.getItem(STORAGE_KEY)

  if (!stored) {
    return {
      remaining: GUEST_DAILY_SEARCH_LIMIT,
      isLimited: false,
      count: 0
    }
  }

  try {
    const tracking: SearchTracking = JSON.parse(stored)

    // Reset if it's a new day - this cleans up stale data automatically
    if (tracking.resetDate !== today) {
      localStorage.removeItem(STORAGE_KEY)
      return {
        remaining: GUEST_DAILY_SEARCH_LIMIT,
        isLimited: false,
        count: 0
      }
    }

    const remaining = GUEST_DAILY_SEARCH_LIMIT - tracking.count
    return {
      remaining: Math.max(0, remaining),
      isLimited: remaining <= 0,
      count: tracking.count
    }
  } catch {
    // Corrupted data - clean up and return fresh state
    localStorage.removeItem(STORAGE_KEY)
    return {
      remaining: GUEST_DAILY_SEARCH_LIMIT,
      isLimited: false,
      count: 0
    }
  }
}

/**
 * Increment the search counter for guest users
 * Called when a search is performed
 */
export function incrementGuestSearchCount(): void {
  if (typeof window === 'undefined') return

  const today = getTodayString()
  const stored = localStorage.getItem(STORAGE_KEY)

  let tracking: SearchTracking = { count: 0, resetDate: today }

  if (stored) {
    try {
      tracking = JSON.parse(stored)
      // Reset if it's a new day
      if (tracking.resetDate !== today) {
        tracking = { count: 0, resetDate: today }
      }
    } catch {
      tracking = { count: 0, resetDate: today }
    }
  }

  tracking.count++
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tracking))
}

/**
 * Reset the guest search counter (useful for testing)
 */
export function resetGuestSearchCount(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem(STORAGE_KEY)
}
